import { Code } from '@lokalise/louis';

import type { Value } from '../types';

const optionsConfig: Record<keyof Value, { label: string | JSX.Element; description: string | JSX.Element }> = {
    switchCompact: {
        label: 'Compact format',
        description:
            'Export only the minimum required structure for use in production. Strip away metadata such as context, comments, and screenshots.',
    },
    switchIncludeDescription: {
        label: 'Include description',
        description: 'Include key descriptions in the exported file (if supported by format).',
    },
    switchIncludeComments: {
        label: 'Include comments',
        description: 'Include key comments and descriptions in the exported file (if supported by format).',
    },
    switchNoLanguageFolders: {
        label: "Don't use directory prefix",
        description: (
            <>
                Use exact folder structure as defined in filenames (instead of creating en/, ru/, fr/ etc. folders in
                the bundle). It is mandatory to have <Code>%LANG_ISO%</Code> placeholder in all filenames in case you
                enable this feature or otherwise files with same name will get overwritten.
            </>
        ),
    },
    switchDisableReferences: {
        label: 'Disable referencing',
        description: (
            <>
                Don&apos;t replace key reference placeholders (e.g. <Code>[%key:hello_world%]</Code>) with their
                corresponding translations.
            </>
        ),
    },
    replaceLineBreaks: {
        label: (
            <>
                Replace line breaks with <Code>\n</Code>
            </>
        ),
        description: (
            <>
                Automatically replace all line breaks with <Code>\n</Code>
            </>
        ),
    },
    switchYamlIncludeRoot: {
        label: 'Language code as root key',
        description: 'Include language ISO code as the root key with exported YAML.',
    },
    jsonUnescapedSlashes: {
        label: 'Unescape forward slashes',
        description: 'Skip escaping forward slashes.',
    },
    addNewlineEof: {
        label: 'Add new line at EOF',
        description: 'Add a new line at the end of the file (if supported by format).',
    },
};

export const optionsKeys: (keyof Value)[] = [
    'switchCompact',
    'switchIncludeDescription',
    'switchIncludeComments',
    'switchNoLanguageFolders',
    'switchDisableReferences',
    'replaceLineBreaks',
    'switchYamlIncludeRoot',
    'jsonUnescapedSlashes',
    'addNewlineEof',
];

const generateOptions = (
    disabledCheckboxes: Record<keyof Value, boolean>,
): {
    value: keyof Value;
    label: string | JSX.Element;
    description: string | JSX.Element;
}[] =>
    optionsKeys
        .filter((key) => !disabledCheckboxes[key])
        .map((key) => ({
            value: key,
            label: optionsConfig[key].label,
            description: optionsConfig[key].description,
        }));

export default generateOptions;
